"use client"

import * as React from "react"
import { X, Plus } from "lucide-react"
import { cn } from "@/utilities/ui"


export interface MenuItem {
  id: string
  title: string
  expandable?: boolean
  href?: string
  items?: MenuItem[]
}

export interface SideMenuProps {
  items: MenuItem[]
  sociallinks?: { icon: string, url: string }[]
  onClose?: () => void
  isOpen?: boolean
}

export function SideMenu({ items, sociallinks, onClose, isOpen = false }: SideMenuProps) {
  const [expandedItems, setExpandedItems] = React.useState<string[]>([])

  const toggleItem = (id: string) => {
    setExpandedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  }

  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isOpen])

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 z-40 transition-opacity duration-300 ease-in-out overflow-y-scroll" onClick={onClose} />
      )}
      <div
        className={cn(
          " fixed inset-y-0 left-0 z-50 w-[300px] bg-card text-primary transform transition-all duration-300 ease-in-out overflow-y-auto",
          isOpen ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0",
        )}
      >
        <div className="flex justify-end p-4">
          <button
            onClick={onClose}
            className="text-primary hover:text-gray-300 transition-colors"
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

       <div className="flex flex-col justify-between min-h-[90vh]">
       <nav className="px-6 flex-grow overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className="border-b border-gray-800">
              <div className="py-4 flex justify-between items-center">
                <a
                  href={item.href || "#"}
                  className="text-sm tracking-wider uppercase hover:text-gray-300 transition-colors"
                >
                  {item.title}
                </a>
                {item.expandable && (
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="text-white hover:text-gray-300 transition-colors"
                    aria-label={`Expand ${item.title}`}
                  >
                    <Plus
                      className={cn(
                        "h-4 w-4 transition-transform duration-300",
                        expandedItems.includes(item.id) ? "rotate-45" : "",
                      )}
                    />
                  </button>
                )}
              </div>
              {item.expandable && (
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-300 ease-in-out",
                    expandedItems.includes(item.id) ? "max-h-48 opacity-100" : "max-h-0 opacity-0",
                  )}
                >
                  <div className="pb-4 transform transition-transform duration-300 ease-in-out">
                    {item.items?.map((subItem) => (
                      <a
                        key={subItem.id} // Use id instead of title for key
                        href={subItem.href || "#"}
                        className="block py-2 pl-4 text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        {subItem.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="p-6">
          <div className="mb-4">
            Keep in touch
          </div>
          <div className="flex items-center justify-center justify-items-center gap-x-3 lg:justify-self-end">
            {sociallinks?.map((link, index) => (
              <a key={index} href={link.url}>
                <i className={`fi ${link.icon}`}></i>
              </a>
            ))}
          </div>
        </div>
       </div>
      </div>
    </>
  )
}

